import React, { useCallback, useMemo, useRef } from 'react';

import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { Button, InputAdornment, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import { DragHandle, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import indexToLetter from '@dyl/utils/indexToLetter';
import Draggable from 'react-draggable';
import useSize from '@dyl/hooks/useSize';
import Node from '@dyl/task-types/Shared/Node';
import { cloneDeep } from "lodash";

const useAnswerStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',

        '&:not(:hover) $arrowsGroup': {
            opacity: 0,
        }
    },
    field: {
        flex: 1,
    },
    arrowsGroup: {
        width: 48,
        height: 56,

        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',

        opacity: 1,
        transitionProperty: 'opacity',
        transitionDuration: '200ms',
    },
    arrow: {
        flex: 1,
    },
}));

export default function Answer({ list, index, answerIndex, pageIndex, isDistractor, ELEMENT_COUNT = 4 }) {
    const classes = useAnswerStyles();
    const [items, setItems] = useTaskField('items');
    const [answer, setAnswer] = useTaskField('items', index, 'answers', answerIndex, 'text');
    const [displayOrder] = useTaskField('items', index, 'answers', answerIndex, 'display_order');
    const localDisplayOrder = ((displayOrder - 1) % ELEMENT_COUNT) + 1;
    const onAnswerChange = useChangeEvent(setAnswer);

    const base = pageIndex * ELEMENT_COUNT;
    const move = useCallback((offset) => {
        if (offset === 0) return;
        const srcIndex = list.findIndex(x => x.index === index && x.answerIndex === answerIndex);
        console.assert(srcIndex !== -1, 'Invalid item');
        const newIndex = srcIndex + offset;
        if (newIndex < 0 || newIndex > ELEMENT_COUNT) return;

        const relevantItemIndices = items.filter(x => x.page === pageIndex + 1).map(x => x.index);

        // Move the element to the new spot and relocate all elements between the old and new location by 1.
        // Splice array and put in the element we want at the appropriate slot.
        list.splice(newIndex, 0, list.splice(srcIndex, 1)[0]);

        setItems(prev => prev.map((item, ii) => {
            if (!relevantItemIndices.includes(item.index)) return item;
            return ({
                ...item,
                answers: item.answers.map((answer, ai) => ({
                    ...answer,
                    display_order: list.findIndex(x => x.index === ii && x.answerIndex === ai) + base + 1,
                })),
            })
        }));
    }, [list, index, answerIndex]);


    const dragHandleClass = `answerDragHandle-${index}`;

    const containerRef = useRef();
    const { height: containerHeight } = useSize(containerRef);

    const bounds = useMemo(() => ({
        top: - (localDisplayOrder - 1) * containerHeight,
        bottom: (ELEMENT_COUNT - localDisplayOrder) * containerHeight,
        left: 0,
        right: 0,
    }), [containerHeight, localDisplayOrder]);

    const distractorsSoFar = cloneDeep(items)
        .splice(0, index)
        .flatMap(x => x.answers)
        .filter(x => !x.is_correct_answer).length;

    const label = isDistractor
        ? `Distractor ${indexToLetter(distractorsSoFar)}`
        : `Answer ${indexToLetter(index)} ${index === 0 ? ' (Example)' : ''}`;

    return (
        <Draggable
            handle={`.${dragHandleClass}`}
            position={{ x: 0, y: 0 }}
            axis='y'
            bounds={bounds}
            onStop={(e, data) => {
                const rect = containerRef.current.getBoundingClientRect();
                const delta = data.lastY;

                const indexOffset = Math.round(delta / rect.height);

                if (indexOffset !== 0) {
                    move(indexOffset);
                }
            }}
        >
            <div className={classes.root} ref={containerRef}>
                <TextField
                    label={label}
                    value={answer}
                    onChange={onAnswerChange}
                    variant='filled'
                    InputProps={{
                        startAdornment: isDistractor ? <Node /> : <Node lineId={index} side='end' />,
                        endAdornment:
                            <InputAdornment position='end' style={{ cursor: 'grab ' }}>
                                <DragHandle className={dragHandleClass} />
                            </InputAdornment>
                        ,
                    }}
                    className={classes.field}
                />

                <div className={classes.arrowsGroup}>
                    <Button
                        className={classes.arrow}
                        onClick={() => move(-1)}
                        disabled={localDisplayOrder=== 1}
                        tabIndex={-1}
                    >
                        <KeyboardArrowUp fontSize='small' />
                    </Button>
                    <Button
                        className={classes.arrow}
                        onClick={() => move(1)}
                        disabled={localDisplayOrder === ELEMENT_COUNT}
                        tabIndex={-1}
                    >
                        <KeyboardArrowDown fontSize='small' />
                    </Button>
                </div>
            </div>
        </Draggable>
    );
}